import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = {
  diakonia_only: {
    title: "The Diakonia Report",
    price: 497,
    subtitle: "Your personalized 10-page identity architecture reading.",
    includes: [
      "10-page Diakonia Report PDF — obsidian & gold",
      "Numerological + destiny archetype mapping from your birth data",
      "Diagnosis across all 5 Pillars (S · P · A · C · E)",
      "Your Borrowed Identity → Covenant Identity map",
      "Emailed to you within minutes of payment confirmation",
    ],
  },
  full_protocol: {
    title: "The Full 6-Month Identity Transformation",
    price: 5000,
    subtitle: "Diakonia Report + the complete 180-day covenant blueprint.",
    includes: [
      "Everything in the Diakonia Report",
      "6-Month S.P.A.C.E. Protocol PDF (~40 pages)",
      "Month 1: full Diagnostic Review",
      "Months 2-6: one Pillar rebuilt per month with daily prompts, weekly milestones, and integration rituals",
      "Combined downloadable identity dossier (Diakonia + Diagnostic + Protocol)",
    ],
  },
};

export default function IntakePortal() {
  const [params] = useSearchParams();
  const initialTier = params.get("tier") === "full_protocol" ? "full_protocol" : "diakonia_only";

  const [tier, setTier] = useState(initialTier);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [place, setPlace] = useState("");
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const selected = useMemo(() => TIERS[tier], [tier]);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/purchase/intake`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        dob,
        place_of_birth: place.trim() || null,
        time_of_birth: time.trim() || null,
        tier,
      });
      setResult(data);
      // scroll to confirmation
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Something went wrong.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    document.title = "Diakonia Intake · S.P.A.C.E. Academy";
  }, []);

  if (result) {
    const priceFmt = `$${(result.price_usd || 0).toLocaleString()}`;
    return (
      <div className="intake-page" data-testid="intake-confirmation">
        <div className="intake-shell">
          <p className="intake-eyebrow">Intake Received</p>
          <h1 className="intake-title">The architecture is anchored.</h1>
          <p className="intake-lede">
            Your intake is on file. Complete your payment of{" "}
            <strong>{priceFmt}</strong> to release your report generation.
          </p>

          <div className="intake-instructions">
            <h3>How to complete your enrollment</h3>
            <ol>
              <li>
                Send <strong>{priceFmt}</strong> via the donation portal:
                <div>
                  <a
                    className="intake-cta-primary"
                    href={result.donation_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="intake-donation-link"
                  >
                    Complete Payment →
                  </a>
                </div>
              </li>
              <li>
                Include this exact memo so we match your payment fast:
                <pre className="intake-memo" data-testid="intake-memo">
                  SPACE {tier === "full_protocol" ? "Full Protocol" : "Diakonia"} — {email.toLowerCase()}
                </pre>
              </li>
              <li>
                Once payment is confirmed, your{" "}
                {tier === "full_protocol" ? "Diakonia Report AND 6-Month Protocol PDFs" : "Diakonia Report PDF"}{" "}
                will be architected and emailed to <strong>{email}</strong> within minutes.
              </li>
            </ol>
          </div>

          <div className="intake-refkey" data-testid="intake-purchase-id">
            <span>Reference ID</span>
            <code>{result.purchase_id}</code>
          </div>

          <p className="intake-fine">
            Questions? Reply to any S.P.A.C.E. Academy email or return to the funnel and open The Architect chat.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-page" data-testid="intake-portal">
      <div className="intake-shell">
        <p className="intake-eyebrow">Intake Portal · S.P.A.C.E. Academy</p>
        <h1 className="intake-title" data-testid="intake-title">
          Anchor your <span className="gold-text">Diakonia Report</span>.
        </h1>
        <p className="intake-lede">
          Give me your name, your birth data, and the tier you are stepping into. I will architect a personalized
          reading of the identity you were designed for — and, if you choose the full transformation, the 6-month
          blueprint that rebuilds it.
        </p>

        {/* Tier selector */}
        <div className="intake-tiers" role="radiogroup" aria-label="Tier">
          {Object.entries(TIERS).map(([k, v]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={tier === k}
              className={`intake-tier ${tier === k ? "is-selected" : ""}`}
              onClick={() => setTier(k)}
              data-testid={`intake-tier-${k}`}
            >
              <div className="intake-tier-head">
                <span className="intake-tier-title">{v.title}</span>
                <span className="intake-tier-price">${v.price.toLocaleString()}</span>
              </div>
              <p className="intake-tier-sub">{v.subtitle}</p>
              <ul className="intake-tier-list">
                {v.includes.map((it, i) => (
                  <li key={i}>{it}</li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        {/* Form */}
        <form className="intake-form" onSubmit={submit}>
          <label className="intake-field">
            <span>Full Name (as given at birth)</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required minLength={2} maxLength={120}
              data-testid="intake-name"
              placeholder="Firstname Middlename Lastname"
            />
          </label>

          <label className="intake-field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="intake-email"
              placeholder="you@example.com"
            />
          </label>

          <div className="intake-row-3">
            <label className="intake-field">
              <span>Date of Birth</span>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                required
                data-testid="intake-dob"
              />
            </label>
            <label className="intake-field">
              <span>Place of Birth</span>
              <input
                type="text"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                maxLength={200}
                data-testid="intake-place"
                placeholder="City, State/Country"
              />
            </label>
            <label className="intake-field">
              <span>Time of Birth (optional)</span>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="intake-time"
              />
            </label>
          </div>

          {error && (
            <div className="intake-error" data-testid="intake-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="intake-submit"
            disabled={submitting}
            data-testid="intake-submit"
          >
            {submitting ? "Anchoring…" : `Reserve → $${selected.price.toLocaleString()}`}
          </button>

          <p className="intake-fine">
            One-time payment · Instant PDF delivery after payment confirmation · Non-refundable.
          </p>
        </form>
      </div>
    </div>
  );
}
